import { AsurionA } from "@expert/common-ui/assets/AsurionA";
import { useCustomerAvatar } from "@expert/contentful/src/hooks";
import { Avatar, Group, Text } from "@mantine/core";
import type { JSX } from "react";
import { useAgentStore } from "@/sdk";
import { userCache } from "@/shared-utils";
import { useFormattedTimestamp } from "../hooks/useFormattedTimeStamp";

const CustomerAvatar = ({ sessionId }: { sessionId: string }) => {
    const customerAvatarUrl = useCustomerAvatar(sessionId);
    return <Avatar src={customerAvatarUrl} size="sm" />;
};

const BotAvatar = () => (
    <Avatar size="sm" color="dark.9" style={{ backgroundColor: "var(--mantine-color-dark-9)" }}>
        <AsurionA />
    </Avatar>
);

/**
 * Renders a formatted timestamp as its own component to isolate frequent timestamp updates
 * and prevent unnecessary re-renders of the parent AuthorBadge
 */
const TimestampDisplay = ({ date }: { date: Date | null }) => {
    const formattedTimestamp = useFormattedTimestamp(date);

    if (!date) return null;

    return (
        <Text size="sm" c="dark.2">
            {formattedTimestamp}
        </Text>
    );
};

interface AuthorBadgeProps {
    senderType: string;
    senderId: string;
    customerName: string;
    sessionId: string;
    dateCreated: Date | null;
}

export function AuthorBadge({
    senderType,
    senderId,
    customerName,
    sessionId,
    dateCreated,
}: AuthorBadgeProps): JSX.Element {
    const agentId = useAgentStore((state) => state.agentId);

    //TODO: handle adding friendly names to each message in the chat SDK
    const authorLabel = () => {
        switch (senderType) {
            case "customer":
                return customerName;
            case "system":
                return "SYSTEM MESSAGE"; // these shouldn't be shown
            case "bot":
                return "Bot";
            case "expert":
                // If senderId is different from the current agentId or the current user's employeeId, show "Previous Expert"
                if (senderId !== agentId && senderId !== userCache.employeeId) {
                    return "Previous expert";
                }
                return "Me";
            default:
                return "Unknown: " + senderId;
        }
    };

    return (
        <Group m={0}>
            {senderType === "customer" && <CustomerAvatar sessionId={sessionId} />}
            {senderType === "bot" && <BotAvatar />}
            <Text size="sm" lineClamp={1} maw={180}>
                {authorLabel()}
            </Text>
            {(senderType === "customer" || senderType === "expert") && <TimestampDisplay date={dateCreated} />}
        </Group>
    );
}
